import LegalLayout, { LegalBlock } from "./LegalLayout";
import { SUPPORT_EMAIL } from "../lib/site";

const Li = ({ children }) => (
  <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-greenaccent" /><span>{children}</span></li>
);

const Email = () => (
  <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-brand hover:underline">{SUPPORT_EMAIL}</a>
);

export default function Privacy() {
  return (
    <LegalLayout testid="privacy-page" label="Legal" title="Privacy Policy" updated="February 2026">
      <p>
        This Privacy Policy explains what information RenewTracker collects, how it is used, and the choices available to you.
      </p>

      <LegalBlock num="1" heading="Data We Collect">
        <ul className="space-y-2">
          <Li><strong>Account information</strong> — your name, business name, email address, and a one-way encrypted (hashed) password. We never store passwords in plain text.</Li>
          <Li><strong>Renewal records you create</strong> — item names, expiry dates, categories, notes, responsible person, and reminder preferences.</Li>
          <Li><strong>Uploaded documents</strong> — PDFs or images you choose to attach to your items, stored in our database in base64-encoded form and scoped to your workspace.</Li>
          <Li><strong>Notification tokens</strong> — push-notification device tokens issued by Apple/Google, stored so we can deliver renewal reminders.</Li>
          <Li><strong>Audit metadata</strong> — actions performed inside your workspace (created, edited, renewed, deleted, invited, joined) with timestamps and user name.</Li>
          <Li><strong>Technical logs</strong> — request timestamps, IP addresses, and error logs used to keep the Service running.</Li>
        </ul>
      </LegalBlock>

      <LegalBlock num="2" heading="Purpose of Data Collection">
        <ul className="space-y-2">
          <Li>Provide the core reminder, tracking, and team features you signed up for.</Li>
          <Li>Send email and push notifications you have configured.</Li>
          <Li>Authenticate you and prevent unauthorized access.</Li>
          <Li>Diagnose, fix bugs, and improve performance.</Li>
          <Li>Comply with legal obligations when required.</Li>
        </ul>
        <p className="italic text-slate-500">We do not sell your personal data. We do not use your data to train AI models.</p>
      </LegalBlock>

      <LegalBlock num="3" heading="Third-Party Service Providers">
        <ul className="space-y-2">
          <Li><strong>Resend</strong> — sends transactional email (password reset, team invites, reminders).</Li>
          <Li><strong>Apple Push / Firebase Cloud Messaging</strong> — relays push notifications to your device.</Li>
          <Li><strong>Cloud hosting providers</strong> — host the database and API.</Li>
        </ul>
        <p>These providers process data only as needed to deliver the Service and are bound by their own security and privacy commitments.</p>
      </LegalBlock>

      <LegalBlock num="4" heading="Data Retention">
        <p>We retain your data for as long as your account is active. Soft-deleted items remain in the database for up to 30 days for recovery, after which they are permanently removed. Backups may contain residual copies of deleted data for up to 30 additional days before they age out of our backup rotation.</p>
      </LegalBlock>

      <LegalBlock num="5" heading="Account Deletion">
        <p>You can delete your account at any time:</p>
        <ul className="space-y-2">
          <Li>Go to <strong>Settings → Delete account</strong>.</Li>
          <Li>If you are the workspace owner and the sole member, deleting your account also deletes the workspace and every related item, renewal record, file, audit entry, and invite.</Li>
          <Li>Workspace owners can also choose <strong>Settings → Delete workspace</strong> to permanently remove the entire workspace and every member.</Li>
          <Li>Account deletion is immediate within the live database; residual copies in backups are removed within 30 days.</Li>
          <Li>You may also email <Email /> to request deletion or to obtain a copy of your data.</Li>
        </ul>
      </LegalBlock>

      <LegalBlock num="6" heading="Security">
        <p>Passwords are stored using a one-way bcrypt hash and are never stored in plain text. Connections to the Service are encrypted in transit. We apply reasonable security measures appropriate to the sensitivity of the data we hold. However, no system can guarantee 100% security. You are responsible for choosing a strong password, keeping it confidential, and notifying us promptly if you suspect compromise.</p>
      </LegalBlock>

      <LegalBlock num="7" heading="Your Rights">
        <p>Depending on your jurisdiction, you may have rights to access, correct, export, restrict, or delete your personal data. To exercise these rights, contact us at <Email />.</p>
      </LegalBlock>

      <LegalBlock num="8" heading="Children">
        <p>RenewTracker is intended for use by businesses and adults. We do not knowingly collect data from children under 16.</p>
      </LegalBlock>

      <LegalBlock num="9" heading="Contact for Privacy Requests">
        <p>Privacy requests, complaints, and questions: <Email />.</p>
      </LegalBlock>
    </LegalLayout>
  );
}
